import React, { useState, useEffect } from 'react';
import { Download, Monitor, Globe, Apple, Smartphone, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@snipit/core/context/AuthContext';
import ConsentWall from '@snipit/ui/components/ConsentWall';

export default function DownloadsView() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [consentGranted, setConsentGranted] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('webPolicyConsent') === 'true') {
      setConsentGranted(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('webPolicyConsent', 'true');
    setConsentGranted(true);
  };

  const handleDecline = () => {
    navigate('/');
  };

  const downloads = [
    {
      name: 'Windows',
      description: 'Native desktop application for Windows 10/11. Built with Tauri.',
      icon: Monitor,
      version: 'v0.1.0',
      type: '.msi / .exe',
      available: true,
      href: '/downloads/snipit-desktop-setup.msi'
    },
    {
      name: 'Chrome Extension',
      description: 'Integrates seamlessly into your browser for quick access.',
      icon: Globe,
      version: 'v2.1.1',
      type: 'Chrome Web Store',
      available: true,
      href: 'https://chromewebstore.google.com/detail/snipit-dev-tool/dodlehfbiakbahnfgcgkbheopmkfnibd'
    },
    {
      name: 'macOS',
      description: 'Native desktop application for Intel and Apple Silicon.',
      icon: Apple,
      version: 'Coming Soon',
      type: '.dmg',
      available: false
    },
    {
      name: 'Mobile App',
      description: 'Access your snippets on the go for iOS and Android.',
      icon: Smartphone,
      version: 'Coming Soon',
      type: 'App Store / Play Store',
      available: false
    }
  ];

  if (!currentUser && !consentGranted) {
    return <ConsentWall onAccept={handleAccept} onDecline={handleDecline} />;
  }

  return (
    <div className={`p-8 h-full overflow-y-auto ${!currentUser ? 'bg-[#0a0a0a] min-h-screen text-gray-200' : ''}`}>
      {!currentUser && (
        <Link to="/" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>
      )}
      <h1 className="text-3xl font-bold text-white mb-2">Downloads</h1>
      <p className="text-gray-400 mb-8">Get SnipIT on all your devices to keep your workflow uninterrupted.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
        {downloads.map((platform) => (
          <div key={platform.name} className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-6 flex flex-col hover:border-gray-700 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className={`p-3 rounded-lg ${platform.available ? 'bg-blue-600/20 text-blue-400' : 'bg-gray-800 text-gray-500'}`}>
                  <platform.icon className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{platform.name}</h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <span>{platform.version}</span>
                    <span>•</span>
                    <span>{platform.type}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-gray-400 text-sm mb-6 flex-1">{platform.description}</p>
            
            {platform.available && platform.href ? (
              <a 
                href={platform.href}
                download={platform.name === 'Windows' ? true : undefined}
                target={platform.name !== 'Windows' ? '_blank' : undefined}
                rel="noreferrer"
                className="w-full py-2.5 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="w-5 h-5" />
                <span>Download Now</span>
              </a>
            ) : (
              <button 
                className={`w-full py-2.5 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${
                  platform.available 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!platform.available}
                onClick={() => {
                  if (platform.available) {
                    alert('Download link not configured yet.');
                  }
                }}
              >
                {platform.available ? (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Download Now</span>
                  </>
                ) : (
                  <span>In Development</span>
                )}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
